/**
 * Goal Game — Fan page (table card). Tap-after-the-goal flow.
 *
 * A Fan watches the game with the phone in a pocket, so this page never asks
 * anyone to keep it open: it polls only while visible, and every state is a
 * dead end with a way out ("tap again after the next goal"). The SERVER decides
 * win or lose; this screen only animates it.
 *
 * ?op=<operator token> adds a one-device demo bar that calls the EXISTING
 * operator endpoints. The token is validated, stripped from the address bar and
 * held in sessionStorage only — never localStorage, never a cookie.
 *
 * THE SCREEN ITSELF lives in components/goalgame/GoalFanView.jsx, so the
 * control page's phone preview renders the very same component a fan sees.
 * This file is the live wiring: polling, PIN, scratch and the operator bar.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { saveErrorKey, takenBy } from '../../components/goalgame/goalSaveError';
import { inviteRef } from '../../components/goalgame/GoalPlayerAccount';
import { Plus, Square, Volume2, VolumeX } from 'lucide-react';
import { GoalFanView, palette } from '../../components/goalgame/GoalFanView';
import { loadFont } from '../../lib/designTokens';
import { playHorn } from '../../components/goalgame/goalHorn';

const API = `${(typeof window !== 'undefined' ? window.location.origin : '')}/api/goal-game`;

// The league bar says the league in the words a fan knows, from the key the
// promotion carries. Nothing is hardcoded per organization.
const leagueLabel = (key) => (key === 'college-football' ? 'NCAA' : String(key || '').toUpperCase());
const DEVICE_KEY = 'goalGameDeviceId';

// The phone remembers the email only as a convenience. The server's table row
// is the source of truth, and this copy is dropped once that row expires.
const storedEmail = (key) => {
    try {
        const raw = localStorage.getItem(key);
        if (!raw) return null;
        const saved = JSON.parse(raw);
        if (saved?.until && saved.until <= new Date().toISOString()) {
            localStorage.removeItem(key);
            return null;
        }
        return saved?.email || null;
    } catch (_e) { return null; }
};

const deviceId = () => {
    let id = localStorage.getItem(DEVICE_KEY);
    if (!id) {
        id = `dev-${Math.random().toString(36).slice(2)}-${Date.now()}`;
        localStorage.setItem(DEVICE_KEY, id);
    }
    return id;
};

export default function GoalFan() {
    const { cardId } = useParams();
    const { t, i18n } = useTranslation();
    const joinKey = `goalGameFan:${cardId}`;
    const opKey = `goalGameOp:${cardId}`;

    const [email, setEmail] = useState('');
    const [screenName, setScreenName] = useState('');
    const [pin, setPin] = useState('');
    const [consent, setConsent] = useState(false);
    const [setupOpen, setSetupOpen] = useState(false);
    // The star pick is part of registration: step 1 is email/PIN/consent,
    // step 2 is the list. Nothing is sent until the final Save.
    const [setupStep, setSetupStep] = useState(1);
    const [starPlayers, setStarPlayers] = useState([]);
    const [starSelected, setStarSelected] = useState(null);
    // The Good luck overview: what the fan just saved, shown once after Save.
    const [goodLuck, setGoodLuck] = useState(null);
    const [joining, setJoining] = useState(false);
    const [error, setError] = useState('');
    const [tonightOpen, setTonightOpen] = useState(false);
    const [lockSeconds, setLockSeconds] = useState(0);
    // The PIN is asked on EVERY goal, so the token is kept per event only.
    const [pinToken, setPinToken] = useState(null);
    const [pinnedEvent, setPinnedEvent] = useState(null);
    const [state, setState] = useState(null);
    const [brand, setBrand] = useState(null);
    const [opToken, setOpToken] = useState(null);
    const [sound, setSound] = useState(() => sessionStorage.getItem(`goalGameSound:${cardId}`) === '1');
    const [opBusy, setOpBusy] = useState(false);
    const [askReset, setAskReset] = useState(false);
    // What the iPad bar needs to say: which promotion is live and what one tap
    // would switch to. Only ever fetched when the bar is present.
    const [opInfo, setOpInfo] = useState(null);
    // DEMO ONLY — which experience the one iPad is showing: on location or at
    // home. It starts fresh on EVERY open ("On location" unless the link says
    // otherwise), so a switch made at the end of one demo can never greet the
    // next person with the wrong experience. The pills still switch it any
    // number of times within the session.
    // A plain ?demo=home / ?demo=bar link opens straight into one of the two,
    // with no operator token and therefore no operator controls at all. The
    // server honours it for a demo organization and ignores it everywhere else.
    const demoFromUrl = (() => {
        try {
            const want = (new URLSearchParams(window.location.search).get('demo') || '')
                .trim().toLowerCase();
            return (want === 'home' || want === 'bar') ? want : null;
        } catch (_e) { return null; }
    })();
    const [demoMode, setDemoMode] = useState(demoFromUrl || null);
    const [checking, setChecking] = useState(false);
    const [scratched, setScratched] = useState(false);
    // Patron Points ON: the server asks for a registered player before the
    // scratch. The account form opens here and the scratch resumes after it.
    const [needAccount, setNeedAccount] = useState(false);
    // A brand-new account has to tap the link in its confirmation email first.
    const [confirmNeeded, setConfirmNeeded] = useState(false);
    const [outcome, setOutcome] = useState(null);
    // JOIN THIS TABLE. This phone is the table's owner if it still holds the
    // set-up email for tonight — the same copy the game already keeps. No new
    // marker, and nothing is asked of the server.
    const isTableOwner = !!storedEmail(joinKey);
    const [joinBusy, setJoinBusy] = useState(false);
    const [joinError, setJoinError] = useState('');
    const playing = useRef(false);
    const lastEvent = useRef(null);
    // How long the SERVER says to wait before asking again. Two seconds until
    // it says otherwise, which is also what happens for ever if the promotion
    // has pacing switched off.
    const waitMs = useRef(2000);

    // The demo's switch travels with every call the demo screen makes; a fan's
    // own phone has no operator token and sends nothing.
    const modeOf = () => (demoFromUrl || ((opToken && demoMode) ? demoMode : null));

    const poll = useCallback(async () => {
        try {
            const want = demoFromUrl || ((opToken && demoMode) ? demoMode : null);
            const mode = want ? `&mode=${want}` : '';
            const patron = localStorage.getItem('patron_device_id') || '';
            const res = await fetch(`${API}/public/${cardId}/state?device=${encodeURIComponent(deviceId())}`
                + `&patron_device=${encodeURIComponent(patron)}${mode}`);
            if (!res.ok) return;
            const data = await res.json();
            waitMs.current = Number(data.next_poll_ms) > 0 ? Number(data.next_poll_ms) : 2000;
            const eventId = data.event?.id || null;
            if (eventId !== lastEvent.current) {
                lastEvent.current = eventId;
                setScratched(false);
                setOutcome(null);
                playing.current = false;
            }
            if (data.played) setOutcome((prev) => prev || { result: data.result, prize: data.prize });
            setState(data);
        } catch (_e) { /* a dropped poll is harmless; the next one is on its way */ }
    }, [cardId, opToken, demoMode, demoFromUrl]);

    // Poll ONLY while the page is visible — a phone in a pocket costs nothing.
    // Each answer sets the wait for the next ask, so a phone at home on a quiet
    // afternoon is not hammering the server, and the moment a game starts the
    // server's own answer puts it straight back on the live rate.
    useEffect(() => {
        let timer = null;
        let stopped = false;
        const tick = async () => {
            if (document.visibilityState === 'visible') await poll();
            if (stopped) return;
            timer = setTimeout(tick, waitMs.current);
        };
        tick();
        const wake = () => {
            // Back on screen: ask at once rather than waiting out a quiet gap.
            if (document.visibilityState === 'visible') {
                clearTimeout(timer);
                tick();
            }
        };
        document.addEventListener('visibilitychange', wake);
        return () => {
            stopped = true;
            clearTimeout(timer);
            document.removeEventListener('visibilitychange', wake);
        };
    }, [poll]);

    const loadBrand = useCallback(async () => {
        try {
            const res = await fetch(`${API}/public/${cardId}/brand`);
            if (res.ok) setBrand(await res.json());
        } catch (_e) { /* branding is cosmetic — the game still runs */ }
    }, [cardId]);

    // Sit down at a table that is already set up. The phone must already have
    // its own points account: that is what the form above the button creates.
    const joinTable = useCallback(async () => {
        setJoinBusy(true);
        setJoinError('');
        try {
            const res = await fetch(`${API}/public/${cardId}/table/join`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    device_id: localStorage.getItem('patron_device_id') || '',
                    table_device_id: deviceId(),
                }),
            });
            if (!res.ok) {
                const body = await res.json().catch(() => ({}));
                setJoinError(String(body.detail || 'join_failed'));
                return;
            }
            await poll();
        } catch (_e) { setJoinError('join_failed'); }
        finally { setJoinBusy(false); }
    }, [cardId, poll]);

    useEffect(() => { loadBrand(); }, [loadBrand]);

    // The list is loaded ONLY when the promotion offers it, so a promotion
    // with the add-on off makes no player call at all. It stays loaded after
    // the table is set up too, because Choose your star is now one of the main
    // page's own screens.
    useEffect(() => {
        if (!state?.star_pick) { setStarPlayers([]); return; }
        let alive = true;
        (async () => {
            try {
                const res = await fetch(`${API}/public/${cardId}/players`);
                if (!res.ok) return;
                const data = await res.json();
                if (alive) setStarPlayers(data.available ? (data.players || []) : []);
            } catch (_e) { /* no list → no pick step, the round is unaffected */ }
        })();
        return () => { alive = false; };
    }, [cardId, state?.star_pick]);

    // ?op= : validate, strip from the address bar, keep in sessionStorage only.
    useEffect(() => {
        (async () => {
            const url = new URL(window.location.href);
            const fromUrl = url.searchParams.get('op');
            if (fromUrl) {
                url.searchParams.delete('op');
                window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
            }
            const token = fromUrl || sessionStorage.getItem(opKey);
            if (!token) return;
            try {
                const r = await fetch(`${API}/operator/${token}`);
                if (r.ok) {
                    sessionStorage.setItem(opKey, token);
                    setOpToken(token);
                    setOpInfo(await r.json());
                }
                else { sessionStorage.removeItem(opKey); setOpToken(null); }
            } catch (_e) { /* leave the bar hidden */ }
        })();
    }, [opKey]);

    // Step 1 → step 2. The same checks as the final Save, so nobody reaches
    // the list with a bad PIN and loses their pick on the way back.
    const starNext = (e) => {
        e.preventDefault();
        setError('');
        if (!email.trim()) { setError(t('goalGame.emailRequired')); return; }
        if (!/^\d{4}$/.test(pin)) { setError(t('goalGame.pinFourDigits')); return; }
        if (!consent) { setError(t('goalGame.consentRequired')); return; }
        setSetupStep(2);
    };

    /** CREATE THE ACCOUNT AND SET THE TABLE UP, with no star yet and no Good
     *  luck screen — the registration's winner Lock in calls this so a
     *  brand-new player really is signed in before their pick is saved. Same
     *  call, same body, same stored fields as the one-shot save; the e-mail and
     *  PIN the player typed are kept, so Back never asks for them again. */
    const saveSetup = async () => {
        setError('');
        if (!/^\d{4}$/.test(pin)) { setError(t('goalGame.pinFourDigits')); return false; }
        if (!consent) { setError(t('goalGame.consentRequired')); return false; }
        setJoining(true);
        try {
            // ONE DEVICE MARK for the whole sequence: the account, both picks
            // and everything the screen reads must agree on who this phone is.
            // A registration always takes the phone over, because the person
            // holding it now is the player.
            try {
                localStorage.setItem('patron_device_id', deviceId());
            } catch (_e) { /* private mode */ }
            // THE PICKS ARE SAVED AGAINST THE PLAYER'S ACCOUNT, so make that
            // account first — the same registration call the Register-your-table
            // card already uses, with the e-mail, PIN and consent the player
            // just typed and nothing extra stored. Then set the table up
            // exactly as before.
            const acc = await fetch(`/api/patron/public/${cardId}/account`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, pin, consent, lang,
                                       screen_name: screenName,
                                       at_home: !!state?.at_home,
                                       ref: inviteRef(),
                                       device_id: deviceId() }),
            });
            if (!acc.ok) {
                // THE REASON, IN PLAIN WORDS — never a bare "try again".
                const out = await acc.json().catch(() => ({}));
                const key = saveErrorKey(acc.status, out.detail);
                const who = takenBy(out.detail);
                setError(who
                    ? t('goalGame.saveErrors.phoneTakenNamed', { player: who })
                    : t(`goalGame.saveErrors.${key}`));
                return false;
            }
            const res = await fetch(`${API}/public/${cardId}/table/setup`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    email, pin, consent, device_id: deviceId(),
                    star_player_id: null,
                    mode: modeOf(),
                }),
            });
            if (!res.ok) {
                const out = await res.json().catch(() => ({}));
                setError(t(`goalGame.saveErrors.${saveErrorKey(res.status, out.detail)}`));
                return false;
            }
            const data = await res.json();
            localStorage.setItem(joinKey, JSON.stringify({ email, until: data.expires_at }));
            setSetupOpen(false);
            setSetupStep(1);
            setPinToken(data.token || null);
            setPinnedEvent(state?.event?.id || null);
            await poll();
            return true;
        } catch (_e) {
            setError(t('goalGame.saveErrors.offline'));
            return false;
        } finally {
            setJoining(false);
        }
    };

    const setUpTable = async (e, withStar = true) => {
        e.preventDefault();
        setError('');
        if (!/^\d{4}$/.test(pin)) { setError(t('goalGame.pinFourDigits')); return; }
        if (!consent) { setError(t('goalGame.consentRequired')); return; }
        setJoining(true);
        try {
            const res = await fetch(`${API}/public/${cardId}/table/setup`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    email, pin, consent, device_id: deviceId(),
                    star_player_id: (withStar && starSelected?.id) || null,
                    mode: modeOf(),
                }),
            });
            if (!res.ok) {
                const out = await res.json().catch(() => ({}));
                setError(t(`goalGame.saveErrors.${saveErrorKey(res.status, out.detail)}`));
                return;
            }
            const data = await res.json();
            // The phone remembers only THAT the table is set up, and only until
            // the set-up itself expires at 03:00 Toronto.
            localStorage.setItem(joinKey, JSON.stringify({ email, until: data.expires_at }));
            setPin('');
            setSetupOpen(false);
            setSetupStep(1);
            // What the fan just saved, for the Good luck screen — read before
            // the selection is cleared, so the star can be named on it.
            setGoodLuck({ star: (withStar && starSelected) || null });
            setStarSelected(null);
            setPinToken(data.token || null);
            setPinnedEvent(state?.event?.id || null);
            await poll();
        } catch (_e) {
            setError(t('goalGame.saveErrors.offline'));
        } finally {
            setJoining(false);
        }
    };

    // The first person's registration also sets the table up, so nobody types
    // an email and a PIN twice. Nothing in goal_tables or the table PIN code
    // changed: this is the SAME set-up call the form already made.
    const registerTable = async (_account, creds) => {
        if (creds?.email && /^\d{4}$/.test(creds?.pin || '') && !state?.table_set_up) {
            try {
                const res = await fetch(`${API}/public/${cardId}/table/setup`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ email: creds.email, pin: creds.pin,
                                           consent: true, device_id: deviceId(),
                                           mode: modeOf() }),
                });
                if (res.ok) {
                    const data = await res.json();
                    localStorage.setItem(joinKey, JSON.stringify({ email: creds.email, until: data.expires_at }));
                    setPinToken(data.token || null);
                    setPinnedEvent(state?.event?.id || null);
                    // The same Good luck screen the table form ends on, so
                    // BOTH registrations finish the same way.
                    setGoodLuck({ star: null });
                    await poll();
                }
            } catch (_e) { /* the table form is still there as the fallback */ }
        }
    };

    const submitPin = async (e) => {
        e.preventDefault();
        setError('');
        if (!/^\d{4}$/.test(pin)) { setError(t('goalGame.pinFourDigits')); return; }
        setJoining(true);
        try {
            const res = await fetch(`${API}/public/${cardId}/table/pin`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ pin, device_id: deviceId(),
                                       event_id: state?.event?.id || null,
                                       mode: modeOf() }),
            });
            const data = await res.json().catch(() => ({}));
            if (res.ok && data.ok) {
                setPin('');
                setPinToken(data.token);
                setPinnedEvent(state?.event?.id || null);
                return;
            }
            setPin('');
            if (res.status === 429) {
                setLockSeconds(data.locked_seconds || 0);
                setError(t('goalGame.pinLocked', { seconds: data.locked_seconds || 0 }));
            } else if (res.status === 409) {
                await poll();                     // the set-up is gone — back to Tonight's game
            } else {
                setError(t('goalGame.pinMismatch'));
            }
        } catch (_e) {
            setError(t('goalGame.pinMismatch'));
        } finally {
            setJoining(false);
        }
    };

    const onScratched = async () => {
        if (playing.current || !state?.event?.id) return;
        playing.current = true;
        setScratched(true);
        try {
            const res = await fetch(`${API}/public/${cardId}/play`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    event_id: state.event.id,
                    device_id: deviceId(),
                    table_token: pinToken,
                    email: storedEmail(joinKey),
                    patron_device_id: localStorage.getItem('patron_device_id') || '',
                    mode: modeOf(),
                }),
            });
            const data = await res.json();
            if (res.status === 403 && data.detail === 'confirm_email') {
                setScratched(false);
                setConfirmNeeded(true);
                return;
            }
            if (res.status === 403 && data.detail === 'sign_in_first') {
                setScratched(false);
                setNeedAccount(true);
                return;
            }
            if (!res.ok) { setOutcome({ closed: true }); return; }
            setOutcome({ result: data.result, prize: data.prize, already: data.already_played });
        } catch (_e) {
            setOutcome({ closed: true });
        } finally {
            playing.current = false;
        }
    };

    // DEMO ONLY — the two pills open on the switch saved on the control page,
    // so the iPad and the page can never disagree. A ?demo= showroom link
    // still wins.
    useEffect(() => {
        if (demoFromUrl || demoMode) return;
        const saved = opInfo?.demo_experience;
        if (saved) setDemoMode(saved);
    }, [opInfo, demoFromUrl, demoMode]);

    const loadBar = async () => {
        if (!opToken) return;
        try {
            const res = await fetch(`${API}/operator/${opToken}`);
            if (res.ok) setOpInfo(await res.json());
        } catch (_e) { /* the bar simply shows no promotion name */ }
    };

    const opAction = async (path) => {
        if (!opToken || opBusy) return;
        setOpBusy(true);
        try {
            await fetch(`${API}/operator/${opToken}${path}`, { method: 'POST' });
            await poll();            // switch the view in ~1 s, no reload
            setTimeout(poll, 900);
        } finally { setOpBusy(false); }
    };

    // ONE tap per league: jump straight to that promotion, in any order. The
    // branding, prizes, wording and league all come from the promotion, so the
    // screen repaints itself from the server — nothing is re-entered, nothing
    // is reset, the card and this link do not change.
    // Start the demo over: one tap asks, the second one does it. Demo
    // organizations only — the server refuses anything else and the button is
    // not even sent to a real venue's screen.
    const resetDemo = async () => {
        if (!opToken || opBusy) return;
        setOpBusy(true);
        try {
            const res = await fetch(`${API}/operator/${opToken}/reset-demo`, { method: 'POST' });
            if (!res.ok) return;
            setAskReset(false);
            // Starting over always goes back to the On location experience.
            setDemoMode('bar');
            // This iPad's own convenience copy of the table's email, so the
            // presenter's screen also goes back to the first tap.
            try { localStorage.removeItem(joinKey); } catch (_e) { /* private mode */ }
            await loadBar();
            await loadBrand();
            await poll();
        } finally { setOpBusy(false); }
    };

    const activatePromotion = async (promotionId) => {
        if (!opToken || opBusy) return;
        setOpBusy(true);
        try {
            const res = await fetch(`${API}/operator/${opToken}/activate`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ promotion_id: promotionId }),
            });
            if (!res.ok) return;
            await loadBar();
            await loadBrand();
            await poll();
        } finally { setOpBusy(false); }
    };

    const checkAgain = async () => {
        setChecking(true);
        await poll();
        setChecking(false);
    };

    // Demo-link sound: one horn per round that OPENS, only when the operator
    // bar is present and the toggle is on. Never on the Fan's own table card.
    const hornedEvent = useRef(null);
    useEffect(() => {
        const ev = state?.event;
        if (!opToken || !sound || !ev || ev.status !== 'open') return;
        if (hornedEvent.current === ev.id) return;
        hornedEvent.current = ev.id;
        playHorn().catch(() => { /* blocked until the next tap — the toggle itself is a tap */ });
    }, [state, opToken, sound]);

    // A pause counts down on screen, so nobody is left guessing.
    useEffect(() => {
        if (lockSeconds <= 0) return;
        const id = setInterval(() => setLockSeconds((n) => {
            if (n <= 1) { setError(''); return 0; }
            return n - 1;
        }), 1000);
        return () => clearInterval(id);
    }, [lockSeconds]);

    // A new goal = the PIN again, by design.
    useEffect(() => {
        const id = state?.event?.id || null;
        if (pinnedEvent && id !== pinnedEvent) { setPinToken(null); setPinnedEvent(null); }
    }, [state, pinnedEvent]);

    useEffect(() => {
        loadFont(brand?.design?.headline_font);
        loadFont(brand?.design?.body_font);
    }, [brand]);

    const toggleSound = () => {
        const next = !sound;
        setSound(next);
        sessionStorage.setItem(`goalGameSound:${cardId}`, next ? '1' : '0');
        if (next) playHorn().catch(() => { /* unlocks audio on this very tap */ });
    };

    const lang = ['fr', 'es'].find((code) => i18n.language?.startsWith(code)) || 'en';
    const setLang = (code) => i18n.changeLanguage(code);
    const c = palette(brand);
    const open = state?.event?.status === 'open';

    // One button per promotion, the main one first: the live league is
    // highlighted and every target is one tap away. Operator token only — a
    // fan opening the same card has no bar at all.
    const leagueBar = (opInfo?.promotions || []).length > 1 ? (
        <div role="group" aria-label={t('goalGame.leagueBarAria')}
             data-testid="goal-op-bar-leagues"
             className="w-full flex justify-center">
            <div className="flex flex-nowrap items-center gap-1.5 rounded-full border border-white/15 bg-black/70 backdrop-blur px-1.5 py-1.5 max-w-full overflow-x-auto no-scrollbar">
                {opInfo.promotions.map((p) => {
                    const isLive = (p.promotion_id || null) === (opInfo.promotion_id || null);
                    return (
                        <button key={p.promotion_id || 'main'} type="button"
                                onClick={() => activatePromotion(p.promotion_id)}
                                disabled={opBusy}
                                aria-current={isLive ? 'true' : undefined}
                                title={p.name}
                                data-testid={`goal-op-bar-league-${p.league}`}
                                style={isLive ? { backgroundColor: c.accent, color: c.bg || '#0f172a' } : undefined}
                                className={`shrink-0 min-h-[44px] min-w-[72px] rounded-full px-5 text-sm font-black uppercase tracking-wide transition-transform duration-200 disabled:opacity-50 ${
                                    isLive ? 'scale-105 shadow-lg' : 'text-white/70 border border-white/20 hover:scale-105'}`}>
                            {leagueLabel(p.league)}
                        </button>
                    );
                })}
            </div>
        </div>
    ) : null;

    // DEMO ONLY — the two experiences, same pills as the league switcher. One
    // tap each way, any number of times; no staff step, no login.
    const modeBar = opInfo?.can_reset_demo ? (
        <div role="group" aria-label={t('goalGame.demoModeAria')}
             data-testid="goal-op-bar-modes"
             className="w-full flex justify-center">
            <div className="flex flex-nowrap items-center gap-1.5 rounded-full border border-white/15 bg-black/70 backdrop-blur px-1.5 py-1.5">
                {[['bar', t('goalGame.demoModeBar')], ['home', t('goalGame.demoModeHome')]].map(([key, label]) => {
                    const on = demoMode === key;
                    return (
                        <button key={key} type="button"
                                onClick={() => {
                                    setDemoMode(key);
                                    // The pills and the control page's switch
                                    // are the same saved choice.
                                    if (opToken) {
                                        fetch(`${API}/operator/${opToken}/demo-experience`, {
                                            method: 'PUT',
                                            headers: { 'Content-Type': 'application/json' },
                                            body: JSON.stringify({ experience: key }),
                                        }).catch(() => { /* the session still switches */ });
                                    }
                                }}
                                aria-pressed={on}
                                data-testid={`goal-op-bar-mode-${key}`}
                                style={on ? { backgroundColor: c.accent, color: c.bg || '#0f172a' } : undefined}
                                className={`shrink-0 min-h-[44px] rounded-full px-5 text-sm font-black uppercase tracking-wide transition-transform duration-200 ${
                                    on ? 'scale-105 shadow-lg' : 'text-white/70 border border-white/20 hover:scale-105'}`}>
                            {label}
                        </button>
                    );
                })}
            </div>
        </div>
    ) : null;

    const resetControl = opInfo?.can_reset_demo ? (
        <div className="w-full flex justify-end">
            {askReset ? (
                <div className="flex flex-wrap items-center justify-end gap-2"
                     data-testid="goal-op-bar-reset-confirm">
                    <span className="text-[11px] text-white/80 max-w-[60%] text-right">
                        {t('goalGame.opResetConfirm')}
                    </span>
                    <button type="button" onClick={resetDemo} disabled={opBusy}
                            data-testid="goal-op-bar-reset-yes"
                            className="min-h-[44px] rounded-full bg-white/90 px-4 text-xs font-black uppercase text-slate-900 disabled:opacity-50">
                        Reset
                    </button>
                    <button type="button" onClick={() => setAskReset(false)} disabled={opBusy}
                            data-testid="goal-op-bar-reset-cancel"
                            className="min-h-[44px] rounded-full border border-white/25 px-4 text-xs font-bold uppercase text-white/70">
                        Cancel
                    </button>
                </div>
            ) : (
                <button type="button" onClick={() => setAskReset(true)} disabled={opBusy}
                        data-testid="goal-op-bar-reset"
                        className="min-h-[44px] rounded-full border border-white/15 px-4 text-[11px] font-bold uppercase tracking-wider text-white/45 hover:text-white/80">
                    Reset demo
                </button>
            )}
        </div>
    ) : null;

    const opBar = opToken ? (
        <div className="fixed bottom-0 left-0 right-0 z-40 border-t border-white/15 bg-black/80 backdrop-blur px-3 py-2 flex flex-col gap-2"
             data-testid="goal-op-bar">
            {leagueBar}
            {modeBar}
            {resetControl}
            <div className="flex flex-wrap gap-2">
            <button onClick={() => opAction('/goal')} disabled={opBusy}
                    data-testid="goal-op-bar-goal"
                    style={{ backgroundColor: c.accent, color: c.bg || '#0f172a' }}
                    className="flex-1 min-w-[140px] rounded-xl py-3 font-black text-lg disabled:opacity-60">
                {t(brand?.league && brand.league !== 'nhl'
                    ? 'goalGame.scoreButton' : 'goalGame.goalButton')}
            </button>
            <button onClick={() => opAction('/extend')} disabled={opBusy || !open}
                    data-testid="goal-op-bar-extend"
                    className="px-4 rounded-xl border border-white/25 text-white text-sm font-bold disabled:opacity-40">
                <Plus size={14} className="inline mr-1" />{t('goalGame.extendButton')}
            </button>
            <button onClick={() => opAction('/end')} disabled={opBusy || !open}
                    data-testid="goal-op-bar-end"
                    className="px-4 rounded-xl border border-white/25 text-white text-sm font-bold disabled:opacity-40">
                <Square size={13} className="inline mr-1" />{t('goalGame.endButton')}
            </button>
            <button onClick={toggleSound} data-testid="goal-op-bar-sound"
                    aria-pressed={sound}
                    className={`px-4 rounded-xl border text-sm font-bold ${
                        sound ? 'border-white/60 text-white' : 'border-white/25 text-white/50'
                    }`}>
                {sound ? <Volume2 size={14} className="inline" /> : <VolumeX size={14} className="inline" />}
            </button>
            </div>
        </div>
    ) : null;

    return (
        <GoalFanView
            brand={brand}
            state={state}
            cardId={cardId}
            lang={lang}
            onLang={setLang}
            opBar={opBar}
            outcome={outcome}
            scratched={scratched}
            pinToken={pinToken}
            tonightOpen={tonightOpen}
            setupOpen={setupOpen}
            email={email}
            pin={pin}
            consent={consent}
            joining={joining}
            error={error}
            lockSeconds={lockSeconds}
            checking={checking}
            onTonightOpen={() => setTonightOpen(true)}
            onTonightBack={() => setTonightOpen(false)}
            onSetupStart={() => { setSetupOpen(true); setSetupStep(1); setError(''); }}
            starOffered={!!state?.star_pick && starPlayers.length > 0}
            starPlayers={starPlayers}
            starStep={setupStep}
            starSelected={starSelected}
            onStarSelect={setStarSelected}
            onStarNext={starNext}
            onStarBack={() => { setSetupStep(1); setError(''); }}
            onStarSkip={(e) => { setStarSelected(null); setUpTable(e, false); }}
            goodLuck={goodLuck}
            onGoodLuckDone={() => setGoodLuck(null)}
            onEmailChange={setEmail}
            screenName={screenName}
            onScreenNameChange={setScreenName}
            onPinChange={(v) => setPin(v.replace(/\D/g, '').slice(0, 4))}
            onConsentChange={setConsent}
            onSetupSubmit={setUpTable}
            onSetupSave={saveSetup}
            onGoodLuck={(star) => setGoodLuck({ star: star || null })}
            onPinSubmit={submitPin}
            onScratched={onScratched}
            onRegistered={registerTable}
            joinUrl={typeof window !== 'undefined' ? `${window.location.origin}/goal/${cardId}` : ''}
            needAccount={needAccount}
            confirmNeeded={confirmNeeded}
            onAccountReady={() => { setNeedAccount(false); onScratched(); }}
            onCheckAgain={checkAgain}
            isTableOwner={isTableOwner}
            joinBusy={joinBusy}
            joinError={joinError}
            onJoinTable={joinTable}
        />
    );
}
